/*
 * ErrorHandler
 *
 * Centralised error handling for image cropping operations. Provides
 * consistent error logging and optional user notifications with
 * support reference codes.
 *
 * Usage:
 *   const handler = new ErrorHandler("MyController")
 *   handler.handle("CODE", "Description", error, true)
 */
export class ErrorHandler {
  /*
   * constructor
   *
   * Creates a new ErrorHandler instance.
   *
   * Input:
   *   prefix {string} - A prefix for error codes, typically the controller name.
   */
  constructor(prefix) {
    this.prefix = prefix
  }

  /*
   * handle
   *
   * Handles an error by logging to console and optionally alerting the user.
   *
   * Inputs:
   *   code      {string}  - Short error code for support reference.
   *   message   {string}  - Human-readable description of the error.
   *   error     {Error}   - The original error object (optional).
   *   showAlert {boolean} - Whether to show an alert to the user. Default: false.
   *
   * Output: none (side effects: console.error, optional alert)
   */
  handle(code, message, error = null, showAlert = false) {
    const fullCode = `${this.prefix}:${code}`
    const fullMessage = `[${fullCode}] ${message}`

    if (error) {
      console.error(fullMessage, error)
    } else {
      console.error(fullMessage)
    }

    if (showAlert) {
      alert(
        `An error occurred: ${message}\n\n` +
        `Please try again or contact support with reference: ${fullCode}`
      )
    }
  }

  /*
   * wrap
   *
   * Wraps an async function with error handling. If the function throws,
   * the error is handled and optionally re-thrown.
   *
   * Inputs:
   *   code      {string}   - Error code to use if the function throws.
   *   message   {string}   - Error message to use.
   *   fn        {Function} - Async function to wrap.
   *   showAlert {boolean}  - Whether to show alert on error. Default: false.
   *   rethrow   {boolean}  - Whether to re-throw the error. Default: true.
   *
   * Output:
   *   {Promise<*>} - Result of the wrapped function, or undefined on error.
   */
  async wrap(code, message, fn, showAlert = false, rethrow = true) {
    try {
      return await fn()
    } catch (error) {
      this.handle(code, message, error, showAlert)
      if (rethrow) throw error
      return undefined
    }
  }
}
